import api from "./api";
import { GetFinancialRecordDto } from "../shared/dtos/FichaFinanceira.dto";
import { RelatorioInadimplenciaDto } from "../shared/dtos/RelatorioInadimplencia.dto";
import { ResultDto } from "../shared/dtos/Result.dto";

class RelatorioService {
  static async getBaixasManuais(
    startDate: string,
    endDate: string
  ): Promise<GetFinancialRecordDto[]> {
    try {
      const response = await api.get<GetFinancialRecordDto[]>("ficha-financeira/baixas-manuais", {
        params: { startDate, endDate },
      });

      if (!response.data) {
        throw new Error("Resposta vazia do servidor");
      }

      // Ordena os pagamentos por data de pagamento
      const sortedPayments = response.data.sort((a, b) => {
        const dateA = a.dataPagamento ? new Date(a.dataPagamento).getTime() : 0;
        const dateB = b.dataPagamento ? new Date(b.dataPagamento).getTime() : 0;
        return dateA - dateB;
      });

      return sortedPayments;
    } catch (error: any) {
      console.error("Erro ao buscar relatório de baixas manuais:", error);
      throw new Error(
        error.response?.data?.message || "Erro ao buscar relatório de baixas manuais"
      );
    }
  }

  static async getBaixasManuaisPaginado(
    page: number,
    limit: number
  ): Promise<{ data: GetFinancialRecordDto[]; total: number }> {
    try {
      const response = await api.get<GetFinancialRecordDto[]>("ficha-financeira/baixas-manuais", {
        params: { page, limit },
      });

      if (!response.data) {
        throw new Error("Resposta vazia do servidor");
      }

      // Como a API retorna array simples, precisamos fazer a paginação manualmente
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = response.data.slice(startIndex, endIndex);

      return {
        data: paginatedData,
        total: response.data.length,
      };
    } catch (error: any) {
      console.error("Erro ao buscar pagamentos manuais:", error);
      throw error;
    }
  }

  static async getMovimentacaoReport(
    dataInicial: Date,
    dataFinal: Date,
    lotacaoId?: string
  ): Promise<any> {
    try {
      const response = await api.get("movimentacao/resumo", {
        params: { dataInicial, dataFinal, lotacaoId },
      });

      if (!response.data) {
        throw new Error("Resposta vazia do servidor");
      }

      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar resumo de movimentações:", error);
      throw new Error(error.response?.data?.message || "Erro ao buscar resumo de movimentações");
    }
  }

  static async getInadimplenciaReport(
    dataInicial: Date,
    dataFinal: Date,
    lotacaoId?: string,
    localPagamentoId?: string,
    acumulaPagamento?: string
  ): Promise<ResultDto> {
    // Validate required parameters
    if (!dataInicial || !dataFinal) {
      throw new Error("Initial and final dates are required parameters");
    }
    const parsedDataInicial = new Date(dataInicial);
    const parsedDataFinal = new Date(dataFinal);

    const parsedLotacaoId = lotacaoId ? parseInt(lotacaoId, 10) : undefined;
    const parsedLocalPagamentoId = localPagamentoId ? parseInt(localPagamentoId, 10) : undefined;
    const parsedAcumulaPagamento = acumulaPagamento
      ? acumulaPagamento.toLowerCase() === "true"
      : undefined;

    const response = await api.get("ficha-financeira/inadimplencia", {
      params: {
        dataInicial: parsedDataInicial,
        dataFinal: parsedDataFinal,
        lotacaoId: parsedLotacaoId,
        localPagamentoId: parsedLocalPagamentoId,
        acumulaPagamento: parsedAcumulaPagamento,
      },
    });

    return response.data;

  }

  static async getBoletosPagosCaixaReport(
    dataInicial: Date,
    dataFinal: Date,
    lotacaoId?: string,
    localPagamentoId?: string
  ): Promise<any> {
    try {
      const response = await api.post("ficha-financeira/relatorio-remessas-pagas-caixa", {
        dataInicial,
        dataFinal,
        idLotacao: lotacaoId,
        idLocalPagamento: localPagamentoId,
      });

      if (!response.data) {
        throw new Error("Não foram encontrados boletos pagos no caixa para o período selecionado");
      }

      // Verificar se a resposta tem a estrutura esperada
      if (response.data && response.data.success === true && response.data.data) {
        const relatorioData = response.data.data;

        // Se não houver fichasFinanceiras, criar um array baseado no exemploRegistro
        if (!Array.isArray(relatorioData.fichasFinanceiras)) {
          // Se temos exemploRegistro e totalRegistros > 0, usar para criar um array
          if (relatorioData.exemploRegistro && relatorioData.totalRegistros > 0) {
            // Criar um array com os registros baseados no exemplo
            // Se temos mais que 1 registro, duplicar o exemplo para ter a quantidade correta
            relatorioData.fichasFinanceiras = Array(relatorioData.totalRegistros)
              .fill(null)
              .map((_, i) => {
                // Para evitar objetos com a mesma referência
                return { ...relatorioData.exemploRegistro };
              });
          } else {
            // Criar uma lista vazia se não tiver exemplo ou totalRegistros for 0
            relatorioData.fichasFinanceiras = [];
          }
        }

        return relatorioData;
      } else if (response.data && response.data.success === false) {
        throw new Error(
          response.data.message ||
          "Não foram encontrados boletos pagos no caixa para o período selecionado"
        );
      }

      // Se chegou aqui, retornar a resposta como está
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar relatório de boletos pagos no caixa:", error);

      // Log detalhado do erro para depuração
      if (error.response) {
        console.error("Detalhes da resposta de erro:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro ao buscar relatório de boletos pagos no caixa";
      throw new Error(errorMessage);
    }
  }

  static async getRecebimentosReport(
    dataInicial: Date,
    dataFinal: Date,
    lotacaoId?: string,
    localPagamentoId?: string
  ): Promise<ResultDto> {
    try {
      const response = await api.get("ficha-financeira/relatorio-recebimentos", {
        params: { dataInicial, dataFinal, lotacaoId, localPagamentoId },
        timeout: 0,
      });

      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar resumo de movimentações:", error);
      throw new Error(error.response?.data?.message || "Erro ao buscar resumo de movimentações");
    }
  }
}

export default RelatorioService;
